/** @format */

import {Box, Button} from '@chakra-ui/react';
import {
	LinkAuthenticationElement,
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import Stripe from 'stripe';
import Spacer from '../../patterns/Spacer';
import {formatAmountFromStripe} from '../../utils';
import {MutationFormMessage} from '../MutationFormMessage';

type CheckoutFormProps = {
	clientSecret: string;
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
	paymentIntent?: Stripe.PaymentIntent | any | undefined;
	stripeReturnUrlSubDirectory: string;
} & Record<string, any>;

export function CheckoutForm({
	clientSecret,
	paymentIntent,
	stripeReturnUrlSubDirectory,
}: CheckoutFormProps) {
	const stripe = useStripe();
	const elements = useElements();
	const [email, setEmail] = useState('');
	const [message, setMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [paymentType, setPaymentType] = useState('');

	useEffect(() => {
		const getPaymentStatus = async () => {
			if (!stripe || !clientSecret) {
				return;
			}

			void stripe
				.retrievePaymentIntent(clientSecret)
				.then(({paymentIntent}) => {
					const {status} = paymentIntent;

					switch (status) {
						case 'succeeded':
							setMessage('Payment successful');
							break;
						case 'processing':
							setMessage('Processing your order...');
							break;
						case 'requires_payment_method':
							setMessage('');
							break;
						case 'requires_confirmation':
							setMessage('Confirm Payment');
							break;
						case 'requires_action':
							setMessage('Authenticating your payment');
							break;
						default:
							setMessage('Something went wrong.');
							break;
					}
				});
		};

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		getPaymentStatus();
	}, [stripe]);

	const handleSubmit = async e => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		e.preventDefault();
		setMessage('');

		if (!stripe || !elements) {
			// stripe.js has not yet loaded.
			// make sure to disable form submission until Stripe.js has loaded.
			return;
		}

		setIsLoading(true);

		const {error} = await stripe.confirmPayment({
			elements,
			confirmParams: {
				// make sure to change this to your payment completion page
				// eslint-disable-next-line @typescript-eslint/naming-convention
				return_url: `${process.env.NEXT_PUBLIC_APP_URL}${stripeReturnUrlSubDirectory}`,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				receipt_email: email,
			},
		});
		// this point will only be reached if there is an immediate error when
		// confirming the payment. Otherwise, your customer will be redirected to
		// your `return_url`. For some payment methods like iDEAL, your customer will
		// be redirected to an intermediate site first to authorize the payment, then
		// redirected to the `return_url`.
		if (error) {
			if (error.type === 'card_error' || error.type === 'validation_error') {
				setMessage(error.message);
			} else {
				console.error(error);
				setMessage('An unexpected error occurred.');
			}
		}

		setIsLoading(false);
	};

	return (
		<form id='payment-form' onSubmit={handleSubmit}>
			<Box mb={{base: '30px'}}>
				<LinkAuthenticationElement
					id='link-authentication-element'
					onChange={e => {
						setEmail(e.value.email);
					}}
				/>
			</Box>
			<PaymentElement
				onChange={event => {
					const {
						value: {type},
					} = event;
					setPaymentType(type);
				}}
				id='payment-element'
				options={{
					layout: 'tabs',
					defaultValues: {
						billingDetails: {
							address: {
								// eslint-disable-next-line @typescript-eslint/naming-convention
								postal_code: ' ',
							},
						},
					},
				}}
			/>
			<Spacer />
			{paymentType === 'bacs_debit' ? (
				<Box color='#285c58' fontSize='14px'>
					By providing your bank details, you allow ASSC to charge your account
					for future payments in accordance with their terms.
				</Box>
			) : null}
			{message ? <MutationFormMessage messages={[message]} /> : null}
			<Spacer />
			{!stripe || !elements ? null : (
				<Button
					w={{base: '100%'}}
					isLoading={isLoading}
					isDisabled={isLoading || !stripe || !elements}
					spinnerPlacement='end'
					loadingText={'Processing payment...'}
					type='submit'
					id='submit'>
					Pay {formatAmountFromStripe(paymentIntent.amount)}
				</Button>
			)}
			<Spacer />
		</form>
	);
}
